// Biome ambience: graveyard rain (screen-space streaks, floor splashes, ceiling drips
// indoors), foundry rising embers, vault floating motes. Purely cosmetic, so
// Math.random() is allowed here. Screen-space particles get a manual parallax drift
// from camera movement so they do not feel glued to the lens.

import type Phaser from 'phaser';
import { DEPTH, GAME_HEIGHT, GAME_WIDTH, TILE } from '../../config/constants';
import type { SectorDef } from '../../core/sector';
import { CELL } from './template';
import type { CellRun, RoomMap } from './tilemap';
import { emberTex, pixelTex, rainTex, softTex } from './textures';

type Emitter = Phaser.GameObjects.Particles.ParticleEmitter;

interface Drifting {
  emitter: Emitter;
  /** Share of camera movement applied to live particles (0 = glued to the screen, 1 = world-locked). */
  follow: number;
}

/** Fade in and out over a particle's life. */
const fadeInOut = (peak: number) => ({
  onUpdate: (_p: Phaser.GameObjects.Particles.Particle, _k: string, t: number) => Math.sin(Math.PI * t) * peak,
});

export class Ambient {
  private emitters: Emitter[] = [];
  private drifting: Drifting[] = [];
  private splash: Emitter | null = null;
  private drip: Emitter | null = null;
  private ceilings: CellRun[] = [];
  private splashRate = 0;
  private splashAcc = 0;
  private dripTimer = 0;
  private lastScrollX = NaN;
  private lastScrollY = NaN;

  constructor(
    private scene: Phaser.Scene,
    private sector: SectorDef,
    private map: RoomMap,
  ) {
    switch (sector.ambient) {
      case 'rain':
        this.buildRain();
        break;
      case 'embers':
        this.buildEmbers();
        break;
      case 'motes':
        this.buildMotes();
        break;
    }
  }

  private add(emitter: Emitter, follow = 0): Emitter {
    this.emitters.push(emitter);
    if (follow > 0) this.drifting.push({ emitter, follow });
    return emitter;
  }

  // ───────────────────────────── Rain ─────────────────────────────

  private buildRain(): void {
    const scene = this.scene;
    const light = this.sector.palette.light;
    const open = this.map.skyOpenness;
    const rain = rainTex(scene);
    if (open > 0.08) {
      const density = 0.25 + 0.75 * open;
      // Distant sheet behind the tiles: thin, dim and slower.
      this.add(
        scene.add
          .particles(0, 0, rain, {
            x: { min: -200, max: GAME_WIDTH + 300 },
            y: { min: -60, max: -10 },
            angle: { min: 101, max: 103 },
            rotate: 12,
            speed: { min: 900, max: 1150 },
            lifespan: { min: 700, max: 1200 },
            scaleX: 0.4,
            scaleY: { min: 0.4, max: 0.7 },
            alpha: { min: 0.08, max: 0.18 },
            tint: light,
            frequency: 1000 / (220 * density),
          })
          .setScrollFactor(0)
          .setDepth(DEPTH.BG_NEAR + 2),
      );
      // Foreground streaks.
      this.add(
        scene.add
          .particles(0, 0, rain, {
            x: { min: -250, max: GAME_WIDTH + 350 },
            y: { min: -80, max: -20 },
            angle: { min: 100, max: 102.5 },
            rotate: 11,
            speed: { min: 1550, max: 1900 },
            lifespan: { min: 520, max: 780 },
            scaleX: { min: 0.5, max: 0.8 },
            scaleY: { min: 0.8, max: 1.4 },
            alpha: { min: 0.14, max: 0.34 },
            tint: light,
            frequency: 1000 / (260 * density),
          })
          .setScrollFactor(0)
          .setDepth(DEPTH.FOREGROUND),
      );
      this.splashRate = 70 * density;
    }
    // Splashes on sky-exposed floor tops near the camera.
    this.splash = this.add(
      scene.add
        .particles(0, 0, pixelTex(scene), {
          emitting: false,
          lifespan: { min: 160, max: 340 },
          speed: { min: 50, max: 170 },
          angle: { min: 215, max: 325 },
          gravityY: 1300,
          scale: { start: 0.55, end: 0.15 },
          alpha: { start: 0.55, end: 0 },
          tint: light,
        })
        .setDepth(DEPTH.FX - 1),
    );
    // Indoors (or under overhangs): drips from ceilings.
    this.ceilings = this.findCeilings();
    if (this.ceilings.length > 0) {
      this.drip = this.add(
        scene.add
          .particles(0, 0, rain, {
            emitting: false,
            lifespan: 1100,
            speedY: { min: 20, max: 60 },
            gravityY: 1500,
            scaleX: 0.45,
            scaleY: 0.35,
            alpha: { start: 0.5, end: 0.1 },
            tint: light,
          })
          .setDepth(DEPTH.WORLD_BACK + 2),
      );
    }
  }

  /** Solid cells with open space below that are not exposed to the sky (rows > 0). */
  private findCeilings(): CellRun[] {
    const m = this.map;
    const out: CellRun[] = [];
    for (let y = 0; y < m.rows - 2; y++) {
      for (let x = 1; x < m.cols - 1; x++) {
        if (m.kindAtTile(x, y) === CELL.SOLID && m.kindAtTile(x, y + 1) === CELL.EMPTY && m.kindAtTile(x, y + 2) === CELL.EMPTY) {
          out.push({ x0: x, x1: x, y });
        }
      }
    }
    return out;
  }

  // ───────────────────────────── Embers / motes ─────────────────────────────

  private buildEmbers(): void {
    const scene = this.scene;
    const p = this.sector.palette;
    const tex = emberTex(scene);
    const cfg = (alpha: number, scale: number, freq: number) => ({
      x: { min: -100, max: GAME_WIDTH + 100 },
      y: { min: GAME_HEIGHT * 0.55, max: GAME_HEIGHT + 40 },
      angle: { min: 250, max: 290 },
      speed: { min: 40, max: 150 },
      accelerationX: { min: -30, max: 30 },
      lifespan: { min: 3000, max: 6000 },
      scale: { start: scale, end: scale * 0.2 },
      alpha: fadeInOut(alpha),
      tint: [p.accent, p.accent2, p.accent],
      blendMode: 'ADD',
      frequency: freq,
    });
    this.add(scene.add.particles(0, 0, tex, cfg(0.8, 0.9, 45)).setScrollFactor(0).setDepth(DEPTH.BG_NEAR + 2), 0.5);
    this.add(scene.add.particles(0, 0, tex, cfg(0.9, 1.4, 260)).setScrollFactor(0).setDepth(DEPTH.FOREGROUND), 1.1);
    // A hot haze glow rising from the bottom of the screen.
    this.add(
      scene.add
        .particles(0, 0, softTex(scene), {
          x: { min: 0, max: GAME_WIDTH },
          y: GAME_HEIGHT + 60,
          speedY: { min: -30, max: -60 },
          lifespan: 6000,
          scale: { min: 3, max: 6 },
          alpha: fadeInOut(0.06),
          tint: p.accent,
          blendMode: 'ADD',
          frequency: 400,
        })
        .setScrollFactor(0)
        .setDepth(DEPTH.BG_NEAR + 1),
    );
  }

  private buildMotes(): void {
    const scene = this.scene;
    const p = this.sector.palette;
    const tex = softTex(scene);
    const cfg = (alpha: number, scale: number, freq: number) => ({
      x: { min: -50, max: GAME_WIDTH + 50 },
      y: { min: -50, max: GAME_HEIGHT + 50 },
      angle: { min: 0, max: 360 },
      speed: { min: 6, max: 30 },
      lifespan: { min: 4000, max: 8000 },
      scale: { min: scale * 0.5, max: scale },
      alpha: fadeInOut(alpha),
      tint: [p.accent, p.accent2, p.light],
      blendMode: 'ADD',
      frequency: freq,
    });
    this.add(scene.add.particles(0, 0, tex, cfg(0.55, 0.16, 70)).setScrollFactor(0).setDepth(DEPTH.BG_NEAR + 2), 0.45);
    this.add(scene.add.particles(0, 0, tex, cfg(0.7, 0.28, 320)).setScrollFactor(0).setDepth(DEPTH.FOREGROUND), 1.05);
  }

  // ───────────────────────────── Frame ─────────────────────────────

  update(dt: number, cam: Phaser.Cameras.Scene2D.Camera): void {
    // Parallax drift for screen-space particles.
    if (!Number.isNaN(this.lastScrollX)) {
      const dx = cam.scrollX - this.lastScrollX;
      const dy = cam.scrollY - this.lastScrollY;
      if (dx !== 0 || dy !== 0) {
        for (const d of this.drifting) {
          for (const p of (d.emitter as unknown as { alive: Phaser.GameObjects.Particles.Particle[] }).alive) {
            p.x -= dx * d.follow;
            p.y -= dy * d.follow;
          }
        }
      }
    }
    this.lastScrollX = cam.scrollX;
    this.lastScrollY = cam.scrollY;

    const view = cam.worldView;
    if (this.splash && this.splashRate > 0 && this.map.skySurfaces.length > 0) {
      this.splashAcc += this.splashRate * dt;
      let guard = 0;
      while (this.splashAcc >= 1 && guard++ < 12) {
        this.splashAcc -= 1;
        const run = this.map.skySurfaces[Math.floor(Math.random() * this.map.skySurfaces.length)];
        const x = (run.x0 + Math.random() * (run.x1 - run.x0 + 1)) * TILE;
        const y = run.y * TILE;
        if (x < view.x - 40 || x > view.right + 40 || y < view.y || y > view.bottom + 20) continue;
        this.splash.emitParticleAt(x, y + 1, 2 + Math.floor(Math.random() * 2));
      }
    }
    if (this.drip && this.ceilings.length > 0) {
      this.dripTimer -= dt;
      if (this.dripTimer <= 0) {
        this.dripTimer = 0.08 + Math.random() * 0.25;
        for (let tries = 0; tries < 6; tries++) {
          const c = this.ceilings[Math.floor(Math.random() * this.ceilings.length)];
          const x = (c.x0 + 0.15 + Math.random() * 0.7) * TILE;
          const y = (c.y + 1) * TILE;
          if (x < view.x || x > view.right || y < view.y - 20 || y > view.bottom) continue;
          this.drip.emitParticleAt(x, y + 2, 1);
          break;
        }
      }
    }
  }

  destroy(): void {
    for (const e of this.emitters) e.destroy();
    this.emitters = [];
    this.drifting = [];
    this.splash = null;
    this.drip = null;
  }
}
